import SortOrder from '~/client/src/shared/enums/SortOrder'

import type { ListSchema } from './schema'
import { resolveBandLevels } from './schema'

export interface ListQuery {
  readonly search: string
  readonly appliedFacets: Readonly<Record<string, readonly string[]>>
  readonly groupBy: readonly string[]
  readonly sortKey: string | null
  readonly sortOrder: SortOrder
  readonly collapsedBandIds: ReadonlySet<string>
  readonly selectedIds: ReadonlySet<string>
}

export type ListQueryPatch = Partial<
  Pick<
    ListQuery,
    'search' | 'appliedFacets' | 'groupBy' | 'sortKey' | 'sortOrder'
  >
>

export type ListAction =
  | { readonly type: 'setSearch'; readonly search: string }
  | {
      readonly type: 'applyFacet'
      readonly key: string
      readonly optionIds: readonly string[]
    }
  | { readonly type: 'resetFilters' }
  | { readonly type: 'setGroupBy'; readonly keys: readonly string[] }
  | {
      readonly type: 'setSort'
      readonly key: string | null
      readonly order: SortOrder
    }
  | { readonly type: 'toggleBand'; readonly id: string }
  | {
      readonly type: 'setCollapseAll'
      readonly bandIds: readonly string[]
      readonly collapsed: boolean
    }
  | { readonly type: 'toggleId'; readonly id: string }
  | {
      readonly type: 'setIdsSelected'
      readonly ids: readonly string[]
      readonly selected: boolean
    }
  | { readonly type: 'clearSelection' }
  | { readonly type: 'patchQuery'; readonly patch: ListQueryPatch }
  | { readonly type: 'resetQuery'; readonly query: ListQuery }

export function initialQuery<T>(
  schema: ListSchema<T>,
  overrides: Partial<ListQuery> = {},
): ListQuery {
  return {
    search: overrides.search ?? '',
    appliedFacets: overrides.appliedFacets ?? {},
    groupBy: clampGroupBy(
      overrides.groupBy ??
        resolveBandLevels(schema).map(level => level.fieldKey),
    ),
    sortKey: overrides.sortKey ?? null,
    sortOrder: overrides.sortOrder ?? SortOrder.DESC,
    collapsedBandIds: overrides.collapsedBandIds ?? new Set<string>(),
    selectedIds: overrides.selectedIds ?? new Set<string>(),
  }
}

export function listQueryReducer(q: ListQuery, a: ListAction): ListQuery {
  switch (a.type) {
    case 'setSearch':
      if (q.search === a.search) return q
      return { ...q, search: a.search }
    case 'applyFacet':
      return applyFacetToQuery(q, a.key, a.optionIds)
    case 'resetFilters':
      if (q.search === '' && Object.keys(q.appliedFacets).length === 0) return q
      return { ...q, search: '', appliedFacets: {} }
    case 'setGroupBy': {
      const groupBy = clampGroupBy(a.keys)
      if (arrayEquals(q.groupBy, groupBy)) return q
      return { ...q, groupBy }
    }
    case 'setSort':
      if (q.sortKey === a.key && q.sortOrder === a.order) return q
      return {
        ...q,
        sortKey: a.key,
        sortOrder:
          a.key && a.order === SortOrder.DEFAULT ? SortOrder.ASC : a.order,
      }
    case 'toggleBand':
      return {
        ...q,
        collapsedBandIds: toggleSetMember(q.collapsedBandIds, a.id),
      }
    case 'setCollapseAll': {
      const collapsedBandIds = setAll(
        q.collapsedBandIds,
        a.bandIds,
        a.collapsed,
      )
      if (collapsedBandIds === q.collapsedBandIds) return q
      return { ...q, collapsedBandIds }
    }
    case 'toggleId':
      return {
        ...q,
        selectedIds: toggleSetMember(q.selectedIds, a.id),
      }
    case 'setIdsSelected': {
      const selectedIds = setAll(q.selectedIds, a.ids, a.selected)
      if (selectedIds === q.selectedIds) return q
      return { ...q, selectedIds }
    }
    case 'clearSelection':
      if (q.selectedIds.size === 0) return q
      return { ...q, selectedIds: new Set<string>() }
    case 'patchQuery':
      return applyQueryPatch(q, a.patch)
    case 'resetQuery':
      return a.query
    default:
      return q
  }
}

export function setSearch(search: string): ListAction {
  return { type: 'setSearch', search }
}

export function applyFacet(
  key: string,
  optionIds: readonly string[],
): ListAction {
  return { type: 'applyFacet', key, optionIds }
}

export function resetFilters(): ListAction {
  return { type: 'resetFilters' }
}

/** @public */
export function setGroupBy(keys: readonly string[]): ListAction {
  return { type: 'setGroupBy', keys }
}

export function setSort(key: string | null, order: SortOrder): ListAction {
  return { type: 'setSort', key, order }
}

export function toggleBand(id: string): ListAction {
  return { type: 'toggleBand', id }
}

export function setCollapseAll(
  bandIds: readonly string[],
  collapsed: boolean,
): ListAction {
  return { type: 'setCollapseAll', bandIds, collapsed }
}

export function toggleId(id: string): ListAction {
  return { type: 'toggleId', id }
}

export function setIdsSelected(
  ids: readonly string[],
  selected: boolean,
): ListAction {
  return { type: 'setIdsSelected', ids, selected }
}

export function clearSelection(): ListAction {
  return { type: 'clearSelection' }
}

export function patchQuery(patch: ListQueryPatch): ListAction {
  return { type: 'patchQuery', patch }
}

export function resetQuery(query: ListQuery): ListAction {
  return { type: 'resetQuery', query }
}

function applyFacetToQuery(
  q: ListQuery,
  key: string,
  optionIds: readonly string[],
): ListQuery {
  const nextFacets: Record<string, readonly string[]> = { ...q.appliedFacets }
  if (optionIds.length === 0) {
    delete nextFacets[key]
  } else {
    nextFacets[key] = [...optionIds]
  }

  if (recordOfArraysEquals(q.appliedFacets, nextFacets)) return q
  return { ...q, appliedFacets: nextFacets }
}

function clampGroupBy(keys: readonly string[]): readonly string[] {
  return keys.slice(0, 1)
}

function toggleSetMember(
  current: ReadonlySet<string>,
  id: string,
): ReadonlySet<string> {
  const next = new Set(current)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  return next
}

function setAll(
  current: ReadonlySet<string>,
  ids: readonly string[],
  selected: boolean,
): ReadonlySet<string> {
  const alreadyApplied = selected
    ? ids.every(id => current.has(id))
    : ids.every(id => !current.has(id))
  if (alreadyApplied) return current

  const next = new Set(current)
  for (const id of ids) {
    if (selected) {
      next.add(id)
    } else {
      next.delete(id)
    }
  }
  return next
}

function stripUndefinedPatch(patch: ListQueryPatch): ListQueryPatch {
  return {
    ...(patch.search !== undefined ? { search: patch.search } : {}),
    ...(patch.appliedFacets !== undefined
      ? { appliedFacets: patch.appliedFacets }
      : {}),
    ...(patch.groupBy !== undefined
      ? { groupBy: clampGroupBy(patch.groupBy) }
      : {}),
    ...(patch.sortKey !== undefined ? { sortKey: patch.sortKey } : {}),
    ...(patch.sortOrder !== undefined ? { sortOrder: patch.sortOrder } : {}),
  }
}

function applyQueryPatch(q: ListQuery, patch: ListQueryPatch): ListQuery {
  const next = { ...q, ...stripUndefinedPatch(patch) }
  return next.sortKey && next.sortOrder === SortOrder.DEFAULT
    ? { ...next, sortOrder: SortOrder.ASC }
    : next
}

function arrayEquals(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  )
}

function recordOfArraysEquals(
  left: Readonly<Record<string, readonly string[]>>,
  right: Readonly<Record<string, readonly string[]>>,
): boolean {
  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)
  return (
    arrayEquals(leftKeys.sort(), rightKeys.sort()) &&
    leftKeys.every(key => arrayEquals(left[key] ?? [], right[key] ?? []))
  )
}
